import { GetComponentQuery } from "apollo"
import {
  DatesTrafficUpdateDto,
  ExtendedTrafficUpdateDto,
  Int32NullableStringKeyValuePair,
  StringStringKeyValuePair,
  TrafficUpdateDto,
} from "../api"
import { extractProperty } from "./extractProperty"

// Re-export StringStringKeyValuePair
export type { StringStringKeyValuePair } from "../api"

export type TrafficUpdate = {
  messageId: number | string
  causeHe: string
  activePeriodStart: string
  activePeriodEnd: string
  startDate: string
  endDate: string
  headerTextHe: string
  lines: string[]
  stationsList: number[]
  descriptionTextHe: string
  itemsList: {
    title: string
    content: string
  }[]
  regularUpdateTag?: string
}

export type TrafficUpdateSinglePage = {
  messageId: number | string
  regularUpdateTag?: string
  causeHe: string
  activePeriodStart: string
  headerTextHe: string
  activePeriodEnd: string
  startDate: string
  endDate: string
  lines: string[]
  type: number
  affectedLines: StringStringKeyValuePair[]
  addMotStop: Int32NullableStringKeyValuePair[]
  canceledMotStop: Int32NullableStringKeyValuePair[]
  stationsList: number[]
  descriptionTextHe: string
  itemsList: {
    title: string
    content: string
  }[]
  datesTrafficUpdateDto?: DatesTrafficUpdateDto[]
}

export function transformMany(data: GetComponentQuery) {
  const edges = data.components?.edges || []
  return edges.map((el) => {
    const node = el.node
    return transformData(node)
  })
}

function extractUpdateId(urlPath: string): string {
  // Remove trailing slash if present
  const path = urlPath.endsWith("/") ? urlPath.slice(0, -1) : urlPath
  // Split by '/' and get the last part
  const parts = path.split("/")
  return parts[parts.length - 1]
}

export function transformData(node: any) {
  const messageId = extractUpdateId(node.absoluteUrl)
  const trafficUpdateBlock = extractProperty(node, "trafficUpdate")?.[0]
  const causeHe = extractProperty(trafficUpdateBlock, "updateReason")
  const regularUpdateTag = extractProperty(
    trafficUpdateBlock,
    "regularUpdateTag",
  )
  const activePeriodStart = extractProperty(trafficUpdateBlock, "startDate")
  const activePeriodEnd = extractProperty(trafficUpdateBlock, "endDate")
  const startDate = extractProperty(trafficUpdateBlock, "startDate")
  const endDate = extractProperty(trafficUpdateBlock, "endDate")
  const linesListBlock = extractProperty(trafficUpdateBlock, "linesList") || []
  const lines =
    linesListBlock.map((item: any) => {
      const line = extractProperty(item, "item")
      return line
    }) || []
  const stationsListBlock =
    extractProperty(trafficUpdateBlock, "stationsList") || []
  const stationsList =
    stationsListBlock.map((item: any) => {
      const station = extractProperty(item, "item")
      return station
    }) || []
  const descriptionTextHe = extractProperty(trafficUpdateBlock, "description")
  const itemsListBlock = extractProperty(node, "itemsList") || []
  const itemsList =
    itemsListBlock.map((x: any) => {
      const title = extractProperty(x, "title")
      const content = extractProperty(x, "content")
      return {
        title,
        content,
      }
    }) || []
  return {
    causeHe,
    regularUpdateTag,
    activePeriodStart,
    activePeriodEnd,
    startDate,
    endDate,
    lines,
    stationsList,
    descriptionTextHe,
    itemsList,
    messageId,
  } as TrafficUpdate
}

export function transformDataSinglePage(node: any) {
  const messageId = extractUpdateId(node.absoluteUrl)
  const trafficUpdateBlock = extractProperty(node, "trafficUpdate")?.[0]
  const causeHe = extractProperty(trafficUpdateBlock, "updateReason")
  const regularUpdateTag = extractProperty(
    trafficUpdateBlock,
    "regularUpdateTag",
  )
  const activePeriodStart = extractProperty(trafficUpdateBlock, "startDate")
  const activePeriodEnd = extractProperty(trafficUpdateBlock, "endDate")
  const startDate = extractProperty(trafficUpdateBlock, "startDate")
  const endDate = extractProperty(trafficUpdateBlock, "endDate")
  const linesListBlock = extractProperty(trafficUpdateBlock, "linesList") || []
  const lines =
    linesListBlock.map((item: any) => {
      const line = extractProperty(item, "item")
      return line
    }) || []
  const stationsListBlock =
    extractProperty(trafficUpdateBlock, "stationsList") || []
  const stationsList =
    stationsListBlock.map((item: any) => {
      const station = extractProperty(item, "item")
      return station
    }) || []
  const descriptionTextHe = extractProperty(trafficUpdateBlock, "description")
  const itemsListBlock = extractProperty(node, "itemsList") || []
  const itemsList =
    itemsListBlock.map((x: any) => {
      const title = extractProperty(x, "title")
      const content = extractProperty(x, "content")
      return {
        title,
        content,
      }
    }) || []
  return {
    causeHe,
    activePeriodStart,
    regularUpdateTag,
    activePeriodEnd,
    startDate,
    endDate,
    lines,
    stationsList,
    descriptionTextHe,
    itemsList,
    messageId,
  } as TrafficUpdateSinglePage
}

export function transformApiToTrafficUpdate(
  apiData: TrafficUpdateDto,
): TrafficUpdate {
  return {
    causeHe: apiData.causeHe,
    activePeriodStart: apiData.activePeriodStart,
    activePeriodEnd: apiData.activePeriodEnd,
    startDate: apiData.activePeriodStart,
    endDate: apiData.activePeriodEnd,
    lines: apiData.lines,
    stationsList: [],
    descriptionTextHe: apiData.headerTextHe,
    itemsList: [],
    messageId: apiData.messageId,
    headerTextHe: apiData.headerTextHe,
  } as TrafficUpdate
}

export function transformApiToTrafficUpdateSinglePage(
  apiData: ExtendedTrafficUpdateDto,
): TrafficUpdateSinglePage {
  return {
    causeHe: apiData.causeHe,
    activePeriodStart: apiData.activePeriodStart,
    activePeriodEnd: apiData.activePeriodEnd,
    startDate: apiData.activePeriodStart,
    endDate: apiData.activePeriodEnd,
    lines: [],
    type: apiData.messageTypeCode,
    affectedLines: apiData.affectedLines,
    addMotStop: apiData.addMotStop,
    canceledMotStop: apiData.canceledMotStop,
    stationsList: [],
    descriptionTextHe: apiData.descriptionTextHe,
    headerTextHe: apiData.headerTextHe,
    itemsList: [],
    messageId: apiData.messageId,
    datesTrafficUpdateDto: apiData.datesTrafficUpdateDto,
  }
}
